import { Box, Container, Typography, Button, Stack } from '@mui/material';
import { ArrowRight, BookOpen, Sparkles } from 'lucide-react';

export function Hero({ data }) {
  const hasCustomColors = Boolean(data.primary_color);
  const primaryColor = data.primary_color || '#3b82f6';
  const secondaryColor = data.secondary_color || '#6366f1';
  
  const heroTitle = data.hero_title || (data.school_name ? `Welcome to ${data.school_name}` : 'Empowering Young Minds for a Brighter Future');
  const heroSubtitle = data.hero_subtitle || data.tagline || null;
  const heroImage = data.hero_image || null;
  const schoolName = data.school_name || '';

  // Generate dynamic, ultra-sleek background gradient based on backend primary/secondary color or fallback sexy dark mesh
  const bgGradient = heroImage
    ? (hasCustomColors
        ? `linear-gradient(135deg, ${primaryColor}F0 0%, ${secondaryColor}D9 100%), url(${heroImage})`
        : `linear-gradient(135deg, rgba(15, 23, 42, 0.92) 0%, rgba(30, 27, 75, 0.88) 50%, rgba(67, 56, 202, 0.82) 100%), url(${heroImage})`)
    : (hasCustomColors
        ? `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
        : `linear-gradient(135deg, #0f172a 0%, #1e1b4b 40%, #312e81 70%, #4338ca 100%)`);

  return (
    <Box
      id="hero"
      sx={{
        position: 'relative',
        minHeight: { xs: '85vh', md: '80vh' },
        display: 'flex',
        alignItems: 'center',
        background: bgGradient,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        color: '#ffffff',
        overflow: 'hidden',
        py: { xs: 8, md: 12 },
      }}
    >
      {/* Decorative Blur Orbs */}
      <Box
        sx={{
          position: 'absolute',
          top: '-15%',
          right: '-5%',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${primaryColor}50 0%, rgba(255,255,255,0) 70%)`,
          filter: 'blur(70px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-20%',
          left: '-5%',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${secondaryColor}40 0%, rgba(255,255,255,0) 70%)`,
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ maxWidth: 720 }}>
          {/* Badge */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.5,
              px: 3,
              py: 1,
              borderRadius: 50,
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              mb: 4,
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }}
          >
            <Sparkles size={20} color="#fbbf24" style={{ filter: 'drop-shadow(0 0 8px rgba(251, 191, 36, 0.6))' }} />
            <Typography variant="subtitle2" fontWeight={800} letterSpacing="0.1em" sx={{ color: '#ffffff', fontSize: '0.85rem' }}>
              WELCOME TO
            </Typography>
          </Box>

          {/* School Name Highlight */}
          {schoolName && (
            <Typography
              variant="h1"
              fontWeight={900}
              sx={{
                fontSize: { xs: '2.5rem', sm: '3rem', md: '4.5rem' },
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                mb: 1.5,
                background: 'linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.3))',
                display: 'block'
              }}
            >
              {schoolName}
            </Typography>
          )}

          {/* Title */}
          {heroTitle && (
            <Typography
              variant="h2"
              fontWeight={700}
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                mb: 2,
                color: 'rgba(255, 255, 255, 0.95)',
                textShadow: '0 4px 15px rgba(0, 0, 0, 0.4)',
              }}
            >
              {heroTitle}
            </Typography>
          )}

          {/* Subtitle */}
          {heroSubtitle && (
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1rem', md: '1.15rem' },
                opacity: 0.9,
                fontWeight: 400,
                lineHeight: 1.6,
                mb: 4,
                maxWidth: 620,
                textShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
                color: '#f8fafc'
              }}
            >
              {heroSubtitle}
            </Typography>
          )}

          {/* CTAs */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              component="a"
              href="#admissions"
              variant="contained"
              size="large"
              endIcon={<ArrowRight size={20} />}
              sx={{
                backgroundColor: '#ffffff',
                color: '#0f172a',
                fontWeight: 800,
                px: 3.5,
                py: 1.2,
                borderRadius: 50,
                fontSize: '1rem',
                textTransform: 'none',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: '#f8fafc',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 15px 30px rgba(0, 0, 0, 0.2)',
                },
              }}
            >
              {data.hero_cta_primary || 'Apply for Admission'}
            </Button>

            <Button
              component="a"
              href="#about"
              variant="outlined"
              size="large"
              startIcon={<BookOpen size={20} />}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.6)',
                color: '#ffffff',
                fontWeight: 700,
                px: 3.5,
                py: 1.2,
                borderRadius: 50,
                borderWidth: 2,
                fontSize: '1rem',
                textTransform: 'none',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderWidth: 2,
                  borderColor: '#ffffff',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.15)',
                },
              }}
            >
              {data.hero_cta_secondary || 'Explore Campus'}
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}

export default Hero;
